// src/features/schedule/components/RelatedTasksSection.tsx
// CurrentScheduleItemCard 內的關聯任務展開/收合區塊

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Task, TaskStatus } from '../../../types/task';
import { CheckCircle, Circle, ChevronUp, ChevronDown } from '../../../components/common/icons';
import { useTaskStatusToggle } from '../../../hooks/useTaskStatusToggle';
import { getCurrentUserId } from '../../../services/auth';
import { logger } from '../../../utils/logger';

interface RelatedTasksSectionProps {
  taskIds: string[];
}

const RelatedTasksSection: React.FC<RelatedTasksSectionProps> = ({ taskIds }) => {
  const userId = getCurrentUserId();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const fetchTasks = useCallback(async () => {
    if (!taskIds.length || !userId) return;

    setIsLoading(true);
    try {
      const { taskService } = await import('../../task');
      const result = await taskService.getTasksByIdsFromCache(
        taskIds,
        userId,
        {
          onCacheHit: (cachedTasks) => {
            setTasks(cachedTasks);
            setIsLoading(false);
          },
          syncInBackground: true
        }
      );
      // result 是快取 + Firestore 合併後的完整版本，有意覆寫 onCacheHit 的暫時結果
      setTasks(result);
    } catch (error) {
      logger.error('RelatedTasksSection', 'Failed to fetch tasks', error);
    } finally {
      setIsLoading(false);
    }
  }, [taskIds, userId]);

  const handleBeforeToggle = useCallback((taskId: string, newStatus: TaskStatus) => {
    setTasks(prev =>
      prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t)
    );
  }, []);

  const handleToggleError = useCallback(() => {
    // 錯誤時重新載入
    fetchTasks();
  }, [fetchTasks]);

  const { toggleTaskStatus } = useTaskStatusToggle(userId, {
    onBeforeToggle: handleBeforeToggle,
    onToggleError: handleToggleError,
  });

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  if (isLoading && tasks.length === 0) {
    return null;
  }

  if (tasks.length === 0) {
    return null;
  }

  const closedCount = tasks.filter(t => t.status === 'CLOSED').length;

  return (
    <div className="mb-4">
      {/* Header: 收合/展開切換 */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(prev => !prev);
        }}
        className="w-full flex items-center justify-between text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors mb-2"
        aria-expanded={isExpanded}
        aria-label={isExpanded ? 'Collapse tasks' : 'Expand tasks'}
      >
        <span>Tasks</span>
        <span className="flex items-center gap-1.5">
          <span className="text-gray-400 font-normal">{closedCount}/{tasks.length}</span>
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {/* 任務列表 */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="rounded-lg bg-white/50 backdrop-blur-sm border border-gray-200/60 divide-y divide-gray-100/60">
              {tasks.map((task) => (
                <motion.button
                  key={task.id}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50/80 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTaskStatus(task);
                  }}
                  initial={false}
                  animate={{ opacity: task.status === 'CLOSED' ? 0.55 : 1 }}
                  transition={{ duration: 0.2 }}
                  aria-label={task.status === 'CLOSED' ? `Mark "${task.title}" as open` : `Mark "${task.title}" as done`}
                >
                  <span className="shrink-0">
                    {task.status === 'CLOSED' ? (
                      <CheckCircle size={16} className="text-green-500" />
                    ) : (
                      <Circle size={16} className="text-gray-400" />
                    )}
                  </span>
                  <span
                    className={`text-sm truncate ${
                      task.status === 'CLOSED'
                        ? 'line-through text-gray-400'
                        : 'text-gray-700'
                    }`}
                  >
                    {task.title}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RelatedTasksSection;
